import React from 'react';

import { Alert, Stack, Switch, Table, Text, Title } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { IconAlertTriangle } from '@tabler/icons-react';
import usePipelines from 'hooks/glmFlows/usePipelines';

import {
  ACTION_BY_TRIGGER,
  ACTION_LABELS,
  GLMKiloFlowTriggers,
  GLMKiloFormValues,
  LIFECYCLE_TRIGGERS,
  TRIGGER_LABELS,
} from '../types';

interface IGLMKiloReviewProps {
  form: UseFormReturnType<GLMKiloFormValues, (values: GLMKiloFormValues) => GLMKiloFormValues>;
}

const GLMKiloReview = ({ form }: IGLMKiloReviewProps): JSX.Element => {
  const { trigger, tcTag, tcPipelineId, tcStageId, acAthleteStatus, enabled } = form.values;
  const isPipelineStage = trigger === GLMKiloFlowTriggers.PIPELINE_STAGE_MOVEMENT;
  const { data: pipelines = [] } = usePipelines(isPipelineStage);

  const triggerLabel = trigger ? TRIGGER_LABELS[trigger] : '-';
  const actionLabel = trigger ? ACTION_LABELS[ACTION_BY_TRIGGER[trigger]] : '-';

  let detail = '-';
  if (isPipelineStage) {
    const pipeline = pipelines.find(p => p.id === tcPipelineId);
    const stage = pipeline?.stages.find(s => s.id === tcStageId);
    detail = [pipeline?.name, stage?.name].filter(Boolean).join(' → ') || '-';
  } else if (trigger === GLMKiloFlowTriggers.TAG_ADDED) {
    detail = tcTag || '-';
  }

  const isLifecycle = !!trigger && LIFECYCLE_TRIGGERS.has(trigger);
  const showDisabledWarning = isLifecycle && !enabled;

  return (
    <Stack sx={{ paddingTop: '20px' }}>
      <Title order={3}>Looks good?</Title>
      <Text size={15}>Review your automation before saving.</Text>
      <Table withBorder withColumnBorders>
        <tbody>
          <tr>
            <td style={{ fontWeight: 600, whiteSpace: 'nowrap', width: '30%' }}>When this happens</td>
            <td>{triggerLabel}</td>
          </tr>
          {detail !== '-' && (
            <tr>
              <td style={{ fontWeight: 600 }}>Details</td>
              <td>{detail}</td>
            </tr>
          )}
          <tr>
            <td style={{ fontWeight: 600 }}>Do this in Kilo</td>
            <td>{actionLabel}</td>
          </tr>
          {isLifecycle && (
            <tr>
              <td style={{ fontWeight: 600 }}>Set status</td>
              <td>{acAthleteStatus || '-'}</td>
            </tr>
          )}
        </tbody>
      </Table>
      <Switch
        label='Activate this automation now'
        description='You can always turn it on or off from your automations list.'
        size='md'
        checked={enabled}
        onChange={event => form.setFieldValue('enabled', event.currentTarget.checked)}
      />
      {showDisabledWarning && (
        <Alert icon={<IconAlertTriangle size={16} />} color='yellow' variant='light'>
          This automation is turned off and won&apos;t run until you activate it.
        </Alert>
      )}
    </Stack>
  );
};

export default GLMKiloReview;
